import { Trans } from "@lingui/react/macro";
import React, { useCallback, useRef } from "react";
import {
  buildSanityImageSrc,
  Button,
  isSanityImagePresent,
  PartialSanityImageType,
  useSanityClient,
  useDebouncedResizeObserver
} from "@silversea-ssc/ui-components-react";
import clsx from "clsx";

import sanityImageUrl from "@sanity/image-url";
import * as styles from "./WidenVideoPlayer.module.scss";
import ClientOnly from "components/ClientOnly";
import WidenBaseVideoPlayer from "./WidenBaseVideoPlayer";

type PlayButtonType = "button" | "icon";

const PlayButton: React.FC<
  React.PropsWithChildren<{ type: PlayButtonType }>
> = (props) => {
  const { children } = props;
  const typeIcon = props.type == "icon";

  return (
    <Button
      className={clsx({
        [styles.customPlayButton]: !typeIcon,
        [styles.customPlayButtonIcon]: typeIcon
      })}
      renderAs="div"
      aria-live="polite"
      tabIndex={0}
      variant={typeIcon ? "text" : "primary"}
      inverted
      iconName="play"
      dataContentPiece="play"
      noWrap
    >
      {!typeIcon && children}
    </Button>
  );
};

export interface WidenVideoPlayerProps {
  videoId: string;
  responsivePoster?: PartialSanityImageType;
  trackingTitle?: string;
  playButtonType?: PlayButtonType;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  controls?: boolean;
  className?: string;
}

const WidenVideoPlayer: React.FC<
  React.PropsWithChildren<WidenVideoPlayerProps>
> = ({
  videoId,
  responsivePoster = undefined,
  trackingTitle = undefined,
  playButtonType = "button",
  autoPlay = false,
  loop = false,
  muted = false,
  controls = true,
  className
}) => {
  const myRef = useRef<HTMLDivElement | null>(null);
  const { ref, width, height } = useDebouncedResizeObserver(1000);
  const fallbackHeight =
    width && width > 0 ? Math.floor(width * (56.25 / 100)) : 562.5;

  const sanityClient = useSanityClient({ useCdn: true });
  const builder = sanityImageUrl(sanityClient);

  const preview = isSanityImagePresent(responsivePoster)
    ? buildSanityImageSrc(builder, responsivePoster.asset, {
        width: width || 1000,
        height: height || fallbackHeight
      })
    : undefined;

  const playButton = (
    <PlayButton key="big-play-button" type={playButtonType}>
      <Trans id="watch the video">watch the video</Trans>
    </PlayButton>
  );

  const onPreviewClick = useCallback(() => {
    const container = myRef.current?.parentElement;
    container?.click();
  }, []);

  return (
    <div
      ref={ref}
      className={clsx([styles.playerWrapper, className])}
      data-tracking-name={trackingTitle}
      data-content-piece="play_video"
    >
      <div ref={myRef}>
        <ClientOnly>
          <WidenBaseVideoPlayer
            trackingTitle={trackingTitle}
            videoId={videoId}
            muted={muted}
            className={styles.reactPlayer}
            playIcon={playButton}
            controls={controls}
            width={width || "100%"}
            height={height || "100%"}
            playing
            loop={loop}
            light={autoPlay ? undefined : preview}
            playsinline={autoPlay}
            onClickPreview={onPreviewClick}
            config={{
              autoPlay: autoPlay,
              muted: muted
            }}
          />
        </ClientOnly>
      </div>
    </div>
  );
};

export default WidenVideoPlayer;
